const PASSWORD_MIN_LENGTH = 8
const PASSWORD_MAX_LENGTH = 64

export const useFormRulesService = () => {
	const $t = useI18n().t

	// helpers
	const validateDecimalPlaces = (number: number, maxDecimalPlaces: number) => {
		const decimalPart = (number.toString().split('.')[1] || '').length
		return decimalPart <= maxDecimalPlaces
	}

	// main rules
	const requiredRule = ((defaultMessage = $t('validation.field_is_required')) => {
		const validateFn = (value: string | number) => {
			return !!value || defaultMessage
		}

		validateFn.withCustomMessage = (customMessage: string) => {
			return (value: string | number) => {
				return !!value || customMessage
			}
		}

		return validateFn
	})()

	const requiredBooleanRule = (value: boolean) => value === true || value === false || $t('validation.field_is_required')

	const numberRule = (value: number) => (!value ? true : !isNaN(value) ? true : $t('validation.field_must_be_a_number'))

	const numberAboveZeroRule = (value: number) => (!value ? true : !isNaN(value) ? true : $t('validation.value_must_be_grather_than_zero'))

	const maxValueRule = (max: number | undefined) => (value: number) => {
		if (!max || !value) return true
		if (Number(value) > max) return `${$t('validation.value_must_be_smaller_or_equal')} ${max}`
		return true
	}

	const minValueRule = (min: number | undefined) => (value: number) => {
		if (!min || !value) return true
		if (Number(value) < min) return `${$t('validation.value_must_be_greater_or_equal')} ${min}`
		return true
	}

	const imageRule = (value: FileList) => {
		if (value && value.length === 0) return $t('validation.image_is_required')
		if (value && value.length > 0 && !value[0].type.match('image.*')) return $t('validation.invalid_image_type')
		return true
	}

	const spaceRule = (value: string) => {
		if (value && value.trim().length === 0) return $t('validation.field_is_required')
		return true
	}

	const numberRequiredRule = (value: number | string) => {
		if (value === undefined || value === null || value === '') return $t('validation.field_is_required')
		if (isNaN(Number(value)) && value !== '0') return $t('validation.field_must_be_a_number')
		return value === '0' ? $t('validation.field_must_be_a_positive_number') : true
	}

	const numberRequiredWithZeroRule = (value: number | string) => {
		if (value === undefined || value === null || value === '') return $t('validation.field_is_required')
		if (isNaN(Number(value)) && value !== '0') return $t('validation.field_must_be_a_number')
		if (Number(value) < 0) return $t('validation.value_must_be_grather_than_zero')
		return true
	}

	const emailRule = ((defaultMessage = $t('validation.email_is_not_valid')) => {
		const validateFn = (value: string) => {
			const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
			return emailRegex.test(value) || defaultMessage
		}

		validateFn.withCustomMessage = (customMessage: string) => {
			return (value: string) => {
				const emailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
				return emailRegex.test(value) || customMessage
			}
		}

		return validateFn
	})()

	const requiredButEmptyRule = (value: string | number) => !!value || value === '' || $t('validation.field_is_required')

	const isPositiveIntegerRule = (value: number) => {
		if (value === undefined || value === null) return true
		if (Number(value) > 0 && Number.isInteger(Number(value))) return true
		return $t('validation.value_must_be_an_integer')
	}

	const floatRule = (precision: number) => (value: number) => {
		const val = Number(value)
		if (val >= 0 && validateDecimalPlaces(val, precision)) return true
		return `${$t('validation.max_precision_is')} (${precision})`
	}

	const maxSignedIntegerRule = (value: number) => {
		if (value >= 42949672.95) return $t('validation.value_out_of_range')
		return true
	}

	const minCharactersRule = (minChars: number) => (value: string) => {
		if (value.length < minChars) return $t('validation.value_must_contains_atleast_chars', { chars: minChars })
		return true
	}

	const maxCharactersRule = (maxChars: number) => (value?: string) => {
		if (value === null || value === undefined) return true
		if (value.length >= maxChars) return $t('validation.value_must_contains_max_chars', { chars: maxChars })
		return true
	}

	const stringMatchRule = (match: string) => (value: string) => {
		if (value !== match) return $t('validation.values_not_identical')
		return true
	}

	const filledArrayRule = (value: any[]) => (value && value.length > 0) || $t('validation.field_is_required')

	const passwordRule = (value: string) => {
		if (!value) return true
		if (value.length < PASSWORD_MIN_LENGTH) return $t('validation.value_must_contains_atleast_chars', { chars: PASSWORD_MIN_LENGTH })
		if (value.length > PASSWORD_MAX_LENGTH) return $t('validation.password_max_chars', { chars: PASSWORD_MAX_LENGTH })
		if (!/\p{Ll}/u.test(value)) return $t('validation.password_lowercase_required')
		if (!/\p{Lu}/u.test(value)) return $t('validation.password_uppercase_required')
		return true
	}

	return {
		requiredRule,
		requiredBooleanRule,
		numberRule,
		numberAboveZeroRule,
		maxValueRule,
		minValueRule,
		imageRule,
		spaceRule,
		numberRequiredRule,
		numberRequiredWithZeroRule,
		emailRule,
		requiredButEmptyRule,
		isPositiveIntegerRule,
		floatRule,
		maxSignedIntegerRule,
		minCharactersRule,
		maxCharactersRule,
		stringMatchRule,
		filledArrayRule,
		passwordRule,
	}
}
